// ONE ROW HEIGHT for every table on References and Rules (owner, 2026-09-11:
// "add a bit more padding between the rows, and make it consistent. It looks
// crowded and maybe inconsistent").
//
// It WAS inconsistent, and not by anyone's choice. Both screens ask Fluent for
// `size="small"`, whose row is 32px with 0 vertical cell padding — tuned for a
// single line of text. Several of these tables do not hold one: Rules → Vendors,
// Companies → Company Aliases and Codes each put a full sentence in the Rule
// column, which wraps to two lines on a narrow window, and Guidance clamps its
// instruction at four. A wrapped row grew while its neighbours stayed at 32px,
// so the same table read as two densities down the page and Rules read as a
// different density from References.
//
// The fix is vertical padding on the CELL rather than a taller fixed row: a
// one-line row gets the breathing space, and a row that wraps keeps the same
// space above and below its text instead of having the extra line eat it.
// `minHeight` on the row is the floor for the one-line case, since padding alone
// leaves Fluent's own 32px in charge there.
//
// A SHARED MODULE, not a copy in each screen, deliberately — and against the
// grain of `RulesScreen.tsx`'s header comment, which says its list primitives
// are copies of `TrainingScreen.tsx`'s on purpose. That reasoning holds for the
// primitives (two screens that may diverge) and fails for this one value: the
// owner's ask is precisely that the two screens NOT diverge here, and two copies
// of a number are how they would. Same shape as `popupPositioning.tsx`'s
// `usePopupListboxClass` — a hook returning one class, so `makeStyles` still
// runs inside a component.
import type React from 'react'
import { makeStyles, tokens } from '@fluentui/react-components'

const useStyles = makeStyles({
  // Fluent's class names are the contract here because the padding has to land
  // on cells this module never renders — every table on both screens builds its
  // own rows. `.fui-TableCell` covers the body; `.fui-TableHeaderCell` keeps the
  // header in step so the head does not sit tighter than what is under it.
  roomy: {
    '& .fui-TableRow': { minHeight: '40px' },
    '& .fui-TableCell': { paddingTop: '10px', paddingBottom: '10px' },
    // The row divider is what makes the extra space read as separation rather
    // than as a taller row. Fluent draws one already; this only keeps it at the
    // subtler of the two neutral strokes now that there is more space around it.
    '& .fui-TableRow:not(:last-child)': { borderBottomColor: tokens.colorNeutralStroke2 },

    // ---- THE HEADER (owner, 2026-09-11: "add some shading or something to the
    // column headers across References and Rules") -------------------------
    //
    // With the rows given room to breathe, the header stopped being the top of
    // the table and started being just the first line of it — nothing but the
    // same divider every other row has, at a moment when the rows around it got
    // further apart. A filled band puts the edge back.
    //
    // The fill is `colorNeutralBackground3`, one step off the page rather than a
    // grey of its own, with a full-strength stroke under it: the header is the
    // one rule on the table that should read as an edge, which is why it uses
    // `colorNeutralStroke1` where the body rows use `2`. A background needs a
    // deliberate dark-theme value — both tokens carry one, which is the reason
    // to reach for tokens here and not a literal.
    '& .fui-TableHeader': { backgroundColor: tokens.colorNeutralBackground3 },
    '& .fui-TableHeaderCell': {
      paddingTop: '8px',
      paddingBottom: '8px',
      backgroundColor: tokens.colorNeutralBackground3,
    },
    '& .fui-TableHeader .fui-TableRow': {
      borderBottomColor: tokens.colorNeutralStroke1,
      backgroundColor: tokens.colorNeutralBackground3,
    },

    // ONE HEADER TYPOGRAPHY FOR BOTH SCREENS, moved here in the same change.
    // Rules' five tables already wore small-caps headers (`rulesHead`, in
    // `RulesScreen.tsx`) and References' did not, so the two screens shaded
    // would still not have matched — and the ask has been consistency between
    // them every time it has come up. This is `rulesHead`'s own rule, applied
    // from the one place both screens already share; that class stays where it
    // is and is now a no-op repeat rather than the only source.
    '& .fui-TableHeaderCell__button': {
      fontSize: '10.5px',
      fontWeight: tokens.fontWeightSemibold,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: tokens.colorNeutralForeground3,
    },
  },
})

/** The class to put on a `<Table>` on References or Rules. Every table on those
    two screens carries it, which is the whole point — see this file's header. */
export function useRoomyTableClass(): string {
  return useStyles().roomy
}

// ---------------------------------------------------------------------------
// THE ROW ITSELF IS THE EDIT CONTROL (owner, 2026-09-11: "can we drop the Edit
// button and pencil and, instead, let users click the row to start editing?").
//
// Every list on References and Rules carried a 64px Edit column holding one
// subtle pencil per row — a column of identical icons whose only job was to
// open the row that was already under the pointer. The row opens it now.
//
// Keyboard parity is not optional here, and it is the whole reason this is a
// hook and a set of props rather than a `cursor: pointer` rule: the pencil was
// a real `<button>`, so it was tabbable and Enter-activated, and a bare
// `onClick` on a `<tr>` would have quietly dropped both. `rowActivationProps`
// gives the row `role="button"`, `tabIndex`, and Enter/Space handling, so what
// the mouse can do the keyboard still can.
const useRowStyles = makeStyles({
  clickable: {
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
    // The focus ring Fluent gives a button, on the row — otherwise a keyboard
    // user tabbing down the list has no idea where they are.
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
  },
})

/** The class that makes a `<TableRow>` look activatable. Pair it with
    `rowActivationProps` — the look without the keyboard handling is the
    regression this comment exists to prevent. */
export function useClickableRowClass(): string {
  return useRowStyles().clickable
}

/** Everything a `<TableRow>` needs to behave like the button it replaced.
    Spread onto the row: `<TableRow {...rowActivationProps(onEdit, label)}>`. */
export function rowActivationProps(
  onActivate: () => void,
  /** What the row opens, for a screen reader — e.g. the rule's own sentence. */
  ariaLabel: string,
) {
  return {
    role: 'button',
    tabIndex: 0,
    'aria-label': ariaLabel,
    onClick: onActivate,
    onKeyDown: (e: React.KeyboardEvent) => {
      // Space scrolls the page by default, and a row that scrolled away as it
      // opened would be worse than one that did nothing.
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onActivate()
      }
    },
  }
}
